import { useEffect, useRef, useState } from 'react';
import Sheet from '@/components/ui/Sheet';
import { ehCelular } from '@/lib/app';
import { CHIPS_SUGESTOES } from '../utils';

interface Props {
  valor: string;
  aoMudar: (v: string) => void;
  aoEnviar: () => void;
  desabilitado?: boolean;
  mensagemDesabilitado?: string | null;
  mostrarBotaoChips: boolean;
  aoEscolherChip: (texto: string) => void;
  /** Botão de imagem (mandar uma foto/arte). */
  mostrarBotaoImagem?: boolean;
  aoEscolherImagem?: () => void;
  placeholder?: string;
}

// Composer fixo embaixo: textarea que cresce (1–5 linhas), botão de enviar e,
// depois da primeira mensagem, um "+" com as mesmas sugestões em folha.
export default function Composer({
  valor,
  aoMudar,
  aoEnviar,
  desabilitado = false,
  mensagemDesabilitado,
  mostrarBotaoChips,
  aoEscolherChip,
  mostrarBotaoImagem = false,
  aoEscolherImagem,
  placeholder = 'Pergunte sobre o evento…',
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [sheetChips, setSheetChips] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const maxAltura = 22 * 5 + 20;
    el.style.height = `${Math.min(el.scrollHeight, maxAltura)}px`;
  }, [valor]);

  function aoTeclar(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !ehCelular()) {
      e.preventDefault();
      if (valor.trim() && !desabilitado) aoEnviar();
    }
  }

  return (
    <>
      <div className="mx-auto flex w-full max-w-[720px] items-end gap-2 rounded-[22px] bg-background-50/95 p-2.5 shadow-xl backdrop-blur-md">
        {mostrarBotaoImagem && (
          <button
            type="button"
            onClick={aoEscolherImagem}
            aria-label="Mandar uma imagem"
            disabled={desabilitado}
            className="aperta flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-background-200/70 disabled:opacity-50"
          >
            <i className="ri-image-add-line text-xl" aria-hidden />
          </button>
        )}
        {mostrarBotaoChips && (
          <button
            type="button"
            onClick={() => setSheetChips(true)}
            aria-label="Sugestões"
            disabled={desabilitado}
            className="aperta flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-background-200/70 disabled:opacity-50"
          >
            <i className="ri-add-line text-xl" aria-hidden />
          </button>
        )}
        <textarea
          ref={ref}
          value={valor}
          onChange={(e) => aoMudar(e.target.value.slice(0, 2000))}
          onKeyDown={aoTeclar}
          disabled={desabilitado}
          rows={1}
          maxLength={2000}
          placeholder={placeholder}
          className="min-h-[44px] max-w-full flex-1 resize-none bg-transparent px-2 py-2.5 text-[15px] leading-[22px] text-foreground-950 outline-none placeholder:text-foreground-400 disabled:opacity-60"
        />
        <button
          type="button"
          onClick={aoEnviar}
          disabled={desabilitado || !valor.trim()}
          aria-label="Enviar"
          className="aperta flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-background-200 disabled:text-foreground-400"
        >
          <i className="ri-send-plane-2-fill text-lg" aria-hidden />
        </button>
      </div>
      {mensagemDesabilitado && (
        <p className="mx-auto mt-1.5 w-full max-w-[720px] px-2 text-center text-[12px] font-medium text-warning-700">
          {mensagemDesabilitado}
        </p>
      )}

      <Sheet aberto={sheetChips} titulo="Sugestões" aoFechar={() => setSheetChips(false)}>
        <div className="flex flex-col gap-2">
          {CHIPS_SUGESTOES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setSheetChips(false);
                aoEscolherChip(c);
              }}
              className="aperta flex min-h-[48px] w-full cursor-pointer items-center rounded-[14px] bg-background-100 px-4 text-left text-[14px] font-medium text-foreground-800 transition-colors hover:bg-background-200/70"
            >
              {c}
            </button>
          ))}
        </div>
      </Sheet>
    </>
  );
}